import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { LiveChatWidget } from '@livechat/widget-react'
import dayjs from 'dayjs'
import tz from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import { sendUserConfigAction } from 'root-redux/actions/user'
import { selectAnswers, selectLanguage } from 'root-redux/selects/common'
import {
  selectGeneralSubscriptionDuration,
  selectGeneralSubscriptionPrice,
  selectGeneralSubscriptionPriceId,
  selectGeneralSubscriptionTrialPeriod,
  selectGeneralSubscriptionTrialPrice,
  selectSecondarySubscriptionFullPrice,
  selectUpsellProduct,
  selectUserPaymentCurrency,
  selectUserPaymentMethod,
  selectUserPaymentSystem,
  selectUserStatus,
} from 'root-redux/selects/user'

import { useCohortToUse } from 'hooks/useCohortToUse'
import { useLiveChatAvailability } from 'hooks/useLiveChatAvailability'

import { usePurchaseStore } from 'modules/purchase/hooks'

import { eventLogger } from 'services/eventLogger.service'
import { googleAnalyticsLogger } from 'services/googleAnalytics.service'

import { PageId } from 'page-constants'
import {
  Cohort,
  FREE_USER_GROUP_ID,
  INTRO_EMAIL_COHORTS,
  LIVE_CHAT_PUBLIC_KEY,
  LiveChatStatus,
  PAID_USER_GROUP_ID,
} from 'root-constants'

dayjs.extend(utc)
dayjs.extend(tz)

export const LiveChat: React.FC = () => {
  const [isNotFirstOpen, setIsNotFirstOpen] = useState(false)

  const dispatch = useDispatch()
  const userAnswersCollection = useSelector(selectAnswers)
  const paymentCurrency = useSelector(selectUserPaymentCurrency)
  const price = useSelector(selectGeneralSubscriptionPrice)
  const priceId = useSelector(selectGeneralSubscriptionPriceId)
  const subscriptionDuration = useSelector(selectGeneralSubscriptionDuration)
  const trialPeriodDays = useSelector(selectGeneralSubscriptionTrialPeriod)
  const trialPrice = useSelector(selectGeneralSubscriptionTrialPrice)
  const secondarySubscriptionFullPrice = useSelector(
    selectSecondarySubscriptionFullPrice,
  )
  const upsellProduct = useSelector(selectUpsellProduct)
  const paymentSystem = useSelector(selectUserPaymentSystem)
  const paymentMethod = useSelector(selectUserPaymentMethod)
  const userStatus = useSelector(selectUserStatus)
  const cohortToUse = useCohortToUse()
  const language = useSelector(selectLanguage)

  const { email, uuid } = usePurchaseStore()

  const { isLiveChatVisible } = useLiveChatAvailability()

  const isSubsDetailsRequired = useMemo(() => {
    return cohortToUse !== Cohort.LUVLY_EMAIL_INTRO_FAST
  }, [cohortToUse])

  const groupId = useMemo(
    () =>
      userStatus?.hasSubscription ? PAID_USER_GROUP_ID : FREE_USER_GROUP_ID,
    [userStatus],
  )

  useEffect(() => {
    const emailCohortVars = {
      email_user: INTRO_EMAIL_COHORTS.includes(cohortToUse),
    }

    const subscriptionVars = {
      subscription_price: price.toString(),
      subscription_duration: subscriptionDuration,
      price_id: priceId,
      trial_period: `${trialPeriodDays}`,
      trial_price: `${trialPrice}`,
      payment_currency: paymentCurrency,
      payment_system: paymentSystem,
      upsell_product: upsellProduct || '',
      luvly_coaching: secondarySubscriptionFullPrice || '',
      payment_method: paymentMethod,
    }

    window.LiveChatWidget.call('update_session_variables', {
      email,
      language,
      username: userAnswersCollection?.[PageId.NAME] || 'No username',
      ...(userStatus?.hasSubscription &&
        isSubsDetailsRequired && { ...subscriptionVars }),
      ...emailCohortVars,
    })

    dispatch(
      sendUserConfigAction({
        email,
        username: userAnswersCollection?.[PageId.NAME] || 'No username',
      }),
    )
  }, [
    cohortToUse,
    dispatch,
    email,
    isSubsDetailsRequired,
    language,
    paymentCurrency,
    paymentMethod,
    paymentSystem,
    price,
    priceId,
    secondarySubscriptionFullPrice,
    subscriptionDuration,
    trialPeriodDays,
    trialPrice,
    upsellProduct,
    userAnswersCollection,
    userStatus?.hasSubscription,
  ])

  useEffect(() => {
    if (isLiveChatVisible) {
      window.LiveChatWidget.call('update_session_variables', {
        livechat_opened: true,
      })

      dispatch(
        sendUserConfigAction({
          livechat_opened: true,
        }),
      )

      window.LiveChatWidget.call('maximize')
    } else {
      window.LiveChatWidget.call('hide')
    }
  }, [dispatch, isLiveChatVisible])

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>
    if (isLiveChatVisible) {
      timerId = setTimeout(() => {
        document.documentElement.style.setProperty(
          '--livechat-visibility',
          'block',
        )

        eventLogger.logLiveChatOpened()
        googleAnalyticsLogger.logLiveChatOpened()

        setIsNotFirstOpen(true)
      }, 1000)
    }
    return () => {
      clearTimeout(timerId)
    }
  }, [isLiveChatVisible])

  const handleHideGreeting = useCallback(() => {
    eventLogger.logLiveChatClosed()
    googleAnalyticsLogger.logLiveChatClosed()
  }, [])

  const handleChatVisibilityChanged = useCallback(
    ({ visibility }) => {
      if (!isLiveChatVisible) return

      if (visibility === LiveChatStatus.MINIMIZED) {
        eventLogger.logLiveChatClosed()
        googleAnalyticsLogger.logLiveChatClosed()
      }

      if (isNotFirstOpen && visibility === LiveChatStatus.MAXIMIZED) {
        eventLogger.logLiveChatOpened()
        googleAnalyticsLogger.logLiveChatOpened()
      }
    },
    [isLiveChatVisible, isNotFirstOpen],
  )

  return (
    <LiveChatWidget
      group={groupId}
      license={LIVE_CHAT_PUBLIC_KEY}
      customerEmail={uuid}
      customerName={userAnswersCollection?.[PageId.NAME] || 'No username'}
      visibility="maximized"
      onGreetingHidden={handleHideGreeting}
      onVisibilityChanged={handleChatVisibilityChanged}
    />
  )
}
